/**
 * @description Two-panel modal for composing and sending estimate emails
 *
 * Key features:
 * - Left panel: email controls (to, cc, bcc, subject, body, send-me-a-copy)
 * - Right panel: live email preview matching actual React Email template layout
 * - Pre-filled from company email template (via outreach templates API)
 * - Template variable interpolation with real estimate data
 * - Headless UI Dialog with enterprise modal pattern (unsaved changes confirm)
 * - Responsive: stacked layout on mobile, side-by-side on lg+
 * - "Attach Estimate PDF" toggle (default ON, matches SendInvoiceModal pattern)
 *
 * @dependencies
 * - @headlessui/react: Dialog + Transition
 * - outreachService: fetch company template defaults
 * - estimateService: send estimate email
 * - useCompany: merchant display name for preview
 *
 * @notes
 * - Mirrors SendInvoiceModal.js (invoice mode) with estimate-specific differences
 * - Template key: 'estimate' (auto-discovered in Outreach > Templates via migration 150)
 * - Template tokens: {merchantName}, {estimateNumber}, {customerName}, {expirationDate}, {totalAmount}
 * - CTA button in preview: "View Estimate" (not "Pay Now")
 * - attachPdf toggle sends boolean in payload → backend generates and attaches PDF
 */

import React, { useState, useEffect, Fragment, useCallback, useMemo, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, Send, Copy, Eye, EyeOff, Mail, CheckCircle2 } from 'lucide-react';
import outreachService from '../../services/api/outreachService';
import estimateService from '../../services/api/estimateService';
import settingsService from '../../services/api/settingsService';
import { useCompany } from '../../contexts/CompanyContext';
import { useBranding } from '../../contexts/BrandingContext';
import { formatCurrency } from '../../utils/estimateHelpers';
import { resolveComposeFromEmail } from '../../utils/composeFromEmail';

// Template token → real estimate value mapping
const buildTokenMap = (estimate, company) => ({
  '{merchantName}': company?.businessName || 'Your Business',
  '{estimateNumber}': estimate?.estimateNumber || '',
  '{customerName}': estimate?.customerName || estimate?.customer?.name || estimate?.company || `${estimate?.firstName || ''} ${estimate?.lastName || ''}`.trim() || 'Customer',
  '{expirationDate}': estimate?.expirationDate
    ? new Date(estimate.expirationDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '',
  '{totalAmount}': formatCurrency(estimate?.totalAmount || 0)
});

const interpolate = (text, tokenMap) => {
  if (!text) return '';
  let result = text;
  Object.entries(tokenMap).forEach(([token, value]) => {
    result = result.replace(new RegExp(token.replace(/[{}]/g, '\\$&'), 'g'), value);
  });
  return result;
};

const TEMPLATE_KEY = 'estimate';
const DEFAULT_SUBJECT = '{merchantName} | Estimate {estimateNumber}';
const DEFAULT_BODY = 'Please find your estimate details below. Click the button to view your estimate and respond online.';

const SendEstimateModal = ({ isOpen, onClose, estimate, onSuccess }) => {
  const { company } = useCompany();
  const { branding, occupancy } = useBranding();

  // All hooks called first, same order every render (patterns.md compliance)
  const [toEmail, setToEmail] = useState('');
  const [ccEmails, setCcEmails] = useState('');
  const [bccEmails, setBccEmails] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sendCopy, setSendCopy] = useState(false);
  const [attachPdf, setAttachPdf] = useState(true);
  const [logoUrl, setLogoUrl] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  // Initial values for dirty tracking
  const [initialSubject, setInitialSubject] = useState('');
  const [initialBody, setInitialBody] = useState('');
  const [fromEmail, setFromEmail] = useState(
    () => resolveComposeFromEmail({ occupancy, company })
  );

  const cancelCloseRef = useRef(null);
  const prevOpenRef = useRef(false);
  const sendResultRef = useRef(null);

  // useMemo for dirty tracking (enterprise pattern)
  const hasUnsavedChanges = useMemo(() => {
    return subject !== initialSubject || body !== initialBody || ccEmails.trim() !== '' || bccEmails.trim() !== '' || !attachPdf || sendCopy;
  }, [subject, body, ccEmails, bccEmails, initialSubject, initialBody, attachPdf, sendCopy]);

  // Token map for preview interpolation
  const tokenMap = useMemo(() => buildTokenMap(estimate, company), [estimate, company]);

  // From address (read-only, resolved server-side)
  const fromDisplay = `"${company?.businessName || 'Your Business'}" <${fromEmail}>`;

  // ---------- Load template defaults ----------

  const loadTemplate = useCallback(async () => {
    try {
      setTemplateLoading(true);
      const [result, logoSetting] = await Promise.all([
        outreachService.getTemplate(TEMPLATE_KEY),
        settingsService.getSetting('email_sender.logo_url').catch(() => null)
      ]);
      const val = result.currentValue || result.defaultValue || {};

      const subj = val.subject || DEFAULT_SUBJECT;
      const bod = val.body || DEFAULT_BODY;
      setSubject(subj);
      setBody(bod);
      setInitialSubject(subj);
      setInitialBody(bod);
      if (result.fromEmail) setFromEmail(result.fromEmail);
      if (logoSetting?.value) setLogoUrl(logoSetting.value);
    } catch {
      const subj = DEFAULT_SUBJECT;
      const bod = DEFAULT_BODY;
      setSubject(subj);
      setBody(bod);
      setInitialSubject(subj);
      setInitialBody(bod);
    } finally {
      setTemplateLoading(false);
    }
  }, []);

  // Initialize only on open transition (false → true)
  useEffect(() => {
    const justOpened = isOpen && !prevOpenRef.current;
    prevOpenRef.current = isOpen;

    if (justOpened && estimate) {
      sendResultRef.current = null;
      setToEmail(estimate.customerEmail || estimate.customer?.email || estimate.email || '');
      setCcEmails('');
      setBccEmails('');
      setError('');
      setSuccess(false);
      setSending(false);
      setShowPreview(false);
      setShowConfirmClose(false);
      setFromEmail(resolveComposeFromEmail({ occupancy, company }));
      loadTemplate();
    }
  }, [isOpen, estimate, loadTemplate, occupancy, company]);

  // ---------- Auto-close on success ----------

  const closeWithResult = useCallback(() => {
    if (onSuccess && sendResultRef.current) {
      onSuccess(sendResultRef.current);
    }
    onClose();
  }, [onSuccess, onClose]);

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(closeWithResult, 2000);
    return () => clearTimeout(timer);
  }, [success, closeWithResult]);

  // ---------- Close handlers (enterprise pattern) ----------

  const handleClose = () => {
    if (success) {
      closeWithResult();
      return;
    }
    if (hasUnsavedChanges) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  const handleForceClose = () => {
    setShowConfirmClose(false);
    onClose();
  };

  // ---------- Send handler ----------

  const handleSend = async () => {
    if (!toEmail.trim()) {
      setError('Recipient email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(toEmail.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setSending(true);
      setError('');

      const payload = {
        toEmail: toEmail.trim(),
        ...(ccEmails.trim() && { ccEmails: ccEmails.split(',').map(e => e.trim()).filter(Boolean) }),
        ...(bccEmails.trim() && { bccEmails: bccEmails.split(',').map(e => e.trim()).filter(Boolean) }),
        subject,
        body,
        sendCopy,
        attachPdf
      };

      const result = await estimateService.sendEstimate(estimate.estimateNumber, payload);

      sendResultRef.current = result;
      setSending(false);
      setSuccess(true);
    } catch (err) {
      console.error('Error sending estimate:', err);
      setError(err.response?.data?.error || 'Failed to send estimate email');
      setSending(false);
    }
  };

  // Early returns AFTER all hooks (patterns.md compliance)
  if (!estimate) return null;

  return (
    <>
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleClose}>
          {/* Backdrop with blur */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className={`relative transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all duration-500 w-full max-h-[90vh] flex flex-col ${success ? 'max-w-sm' : 'max-w-4xl'}`}>
                  {/* Header */}
                  <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Mail className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <Dialog.Title as="h2" className="text-lg font-semibold text-gray-900">
                          Send Estimate
                        </Dialog.Title>
                        <p className="text-sm text-gray-500">
                          {estimate.estimateNumber} to {estimate.customerName || estimate.company || `${estimate.firstName || ''} ${estimate.lastName || ''}`.trim() || 'customer'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleClose}
                      aria-label="Close send estimate modal"
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Scrollable Content */}
                  <div className="flex-1 overflow-y-auto">
                    {/* Success state */}
                    {success ? (
                      <div className="flex items-center justify-center py-20">
                        <div className="text-center">
                          <div className="mx-auto w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-4">
                            <CheckCircle2 className="w-8 h-8 text-green-600" />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">Estimate Sent</h3>
                          <p className="text-sm text-gray-500">
                            {estimate.estimateNumber} sent to {toEmail}
                          </p>
                        </div>
                      </div>
                    ) : templateLoading ? (
                      <div className="flex items-center justify-center py-16">
                        <div className="text-center">
                          <div className="animate-spin w-8 h-8 border-4 border-brand border-t-transparent rounded-full mx-auto mb-3" />
                          <p className="text-sm text-gray-500 font-medium">Loading template...</p>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
                        {/* ====== Left Panel — Email Controls ====== */}
                        <div className="p-6 space-y-4">
                          {/* Error */}
                          {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                              <p className="text-sm text-red-700">{error}</p>
                            </div>
                          )}

                          {/* From (read-only) */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                              {fromDisplay}
                            </div>
                          </div>

                          {/* To */}
                          <div>
                            <label htmlFor="estimate-send-to-email" className="block text-sm font-medium text-gray-700 mb-1">
                              To <span className="text-red-500">*</span>
                            </label>
                            <input
                              id="estimate-send-to-email"
                              type="email"
                              value={toEmail}
                              onChange={(e) => setToEmail(e.target.value)}
                              placeholder="customer@example.com"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand focus:border-brand"
                            />
                          </div>

                          {/* CC */}
                          <div>
                            <label htmlFor="estimate-send-cc-emails" className="block text-sm font-medium text-gray-700 mb-1">CC</label>
                            <input
                              id="estimate-send-cc-emails"
                              type="text"
                              value={ccEmails}
                              onChange={(e) => setCcEmails(e.target.value)}
                              placeholder="cc1@example.com, cc2@example.com"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand focus:border-brand"
                            />
                            <p className="text-xs text-gray-400 mt-1">Separate multiple addresses with commas</p>
                          </div>

                          {/* BCC */}
                          <div>
                            <label htmlFor="estimate-send-bcc-emails" className="block text-sm font-medium text-gray-700 mb-1">BCC</label>
                            <input
                              id="estimate-send-bcc-emails"
                              type="text"
                              value={bccEmails}
                              onChange={(e) => setBccEmails(e.target.value)}
                              placeholder="bcc@example.com"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand focus:border-brand"
                            />
                          </div>

                          {/* Subject */}
                          <div>
                            <label htmlFor="estimate-send-subject" className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                            <input
                              id="estimate-send-subject"
                              type="text"
                              value={subject}
                              onChange={(e) => setSubject(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand focus:border-brand"
                            />
                          </div>

                          {/* Body */}
                          <div>
                            <label htmlFor="estimate-send-body" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                            <textarea
                              id="estimate-send-body"
                              value={body}
                              onChange={(e) => setBody(e.target.value)}
                              rows={4}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand focus:border-brand resize-y"
                            />
                          </div>

                          {/* Send me a copy */}
                          <label htmlFor="estimate-send-copy-toggle" className="flex items-center gap-2 cursor-pointer">
                            <input
                              id="estimate-send-copy-toggle"
                              type="checkbox"
                              checked={sendCopy}
                              onChange={(e) => setSendCopy(e.target.checked)}
                              className="rounded border-gray-300 text-brand focus:ring-brand"
                            />
                            <div className="flex items-center gap-1.5">
                              <Copy className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-700">Send me a copy</span>
                            </div>
                          </label>

                          {/* Attach Estimate PDF */}
                          <label htmlFor="estimate-attach-pdf-toggle" className="flex items-center gap-2 cursor-pointer">
                            <input
                              id="estimate-attach-pdf-toggle"
                              type="checkbox"
                              checked={attachPdf}
                              onChange={(e) => setAttachPdf(e.target.checked)}
                              className="rounded border-gray-300 text-brand focus:ring-brand"
                            />
                            <div>
                              <span className="text-sm text-gray-700">Attach Estimate PDF</span>
                              <p className="text-xs text-gray-400">Include a PDF copy of the estimate as an attachment</p>
                            </div>
                          </label>

                          {/* Mobile preview toggle */}
                          <button
                            type="button"
                            onClick={() => setShowPreview(prev => !prev)}
                            className="lg:hidden w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
                          >
                            {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            {showPreview ? 'Hide Preview' : 'Show Preview'}
                          </button>
                        </div>

                        {/* ====== Right Panel — Live Preview ====== */}
                        <div className={`${showPreview ? 'block' : 'hidden'} lg:block bg-gray-100 p-6`}>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Email Preview</h4>

                          <div className="max-w-sm mx-auto bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                            {/* Header */}
                            <div className="px-5 pt-5 pb-3 text-center">
                              {logoUrl ? (
                                <img src={logoUrl} alt={company?.businessName || 'Logo'} className="max-h-12 mx-auto mb-2" />
                              ) : (
                                <h2 className="text-base font-bold text-gray-900">
                                  {company?.businessName || 'Your Business'}
                                </h2>
                              )}
                            </div>

                            {/* Subject preview */}
                            <div className="px-5 pb-2">
                              <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Subject</p>
                              <p className="text-sm font-semibold text-gray-900">
                                {interpolate(subject, tokenMap) || 'Your subject line will appear here'}
                              </p>
                            </div>

                            {/* Body preview */}
                            <div className="px-5 py-3">
                              <p className="text-sm font-medium text-gray-900 mb-1.5">
                                Hi {tokenMap['{customerName}']},
                              </p>
                              <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">
                                {interpolate(body, tokenMap) || 'Your message will appear here.'}
                              </p>
                            </div>

                            {/* Estimate summary */}
                            <div className="px-5 py-3 border-t border-gray-100">
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-gray-500">Estimate</span>
                                <span className="font-medium text-gray-900">{estimate.estimateNumber}</span>
                              </div>
                              {estimate.expirationDate && (
                                <div className="flex justify-between text-sm mb-1">
                                  <span className="text-gray-500">Expires</span>
                                  <span className="font-medium text-gray-900">{tokenMap['{expirationDate}']}</span>
                                </div>
                              )}

                              {/* Line items */}
                              {estimate.items && estimate.items.length > 0 && (
                                <table className="w-full text-xs mt-3">
                                  <thead>
                                    <tr className="border-b border-gray-200">
                                      <th className="text-left py-1 text-gray-500 font-medium">Item</th>
                                      <th className="text-right py-1 text-gray-500 font-medium">Qty</th>
                                      <th className="text-right py-1 text-gray-500 font-medium">Price</th>
                                    </tr>
                                  </thead>
                                  <tbody className="text-gray-600">
                                    {estimate.items.slice(0, 5).map((item, idx) => (
                                      <tr key={idx} className="border-b border-gray-50">
                                        <td className="py-1 truncate max-w-[120px]">{item.itemName || item.description}</td>
                                        <td className="text-right py-1">{item.quantity}</td>
                                        <td className="text-right py-1">{formatCurrency(item.unitPrice || 0)}</td>
                                      </tr>
                                    ))}
                                    {estimate.items.length > 5 && (
                                      <tr>
                                        <td colSpan={3} className="py-1 text-gray-400 text-center">
                                          +{estimate.items.length - 5} more items
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              )}

                              <div className="flex justify-between mt-2 pt-2 border-t border-gray-200 text-sm font-bold text-gray-900">
                                <span>Total</span>
                                <span>{formatCurrency(estimate.totalAmount || 0)}</span>
                              </div>
                            </div>

                            {/* View Estimate button */}
                            <div className="px-5 py-4 text-center">
                              <div
                                className="inline-block px-6 py-2 text-white text-sm font-semibold rounded-md"
                                style={{ backgroundColor: branding.colors?.primaryColor || '#18181b' }}
                              >
                                View Estimate
                              </div>
                            </div>

                            {/* Footer */}
                            <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 text-center">
                              <p className="text-xs text-gray-500 font-medium">{company?.businessName || 'Your Business'}</p>
                              {company?.addressLine1 && (
                                <p className="text-xs text-gray-400 mt-0.5">
                                  {company.addressLine1}{company.addressLine2 ? `, ${company.addressLine2}` : ''}{company.city ? `, ${company.city}` : ''}{company.state ? `, ${company.state}` : ''} {company.postalCode || ''}
                                </p>
                              )}
                              <p className="text-xs text-gray-400 mt-1">Powered by {branding.identity?.platformName || 'Lynx'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Sticky Footer — Actions (hidden during success) */}
                  {!templateLoading && !success && (
                    <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
                      <button
                        type="button"
                        onClick={handleClose}
                        disabled={sending}
                        className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSend}
                        disabled={sending || !toEmail.trim()}
                        className="flex items-center gap-2 px-6 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {sending ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            Send Estimate
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Unsaved Changes Confirm Dialog */}
      <Transition.Root show={showConfirmClose} as={Fragment}>
        <Dialog as="div" className="relative z-[60]" onClose={() => setShowConfirmClose(false)} initialFocus={cancelCloseRef}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-[70] overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white px-6 py-5 shadow-2xl transition-all w-full max-w-md">
                  <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                    Discard Email?
                  </Dialog.Title>
                  <p className="mt-2 text-sm text-gray-500">
                    You've made changes to this email. Are you sure you want to close without sending?
                  </p>
                  <div className="mt-5 flex justify-end gap-3">
                    <button
                      ref={cancelCloseRef}
                      onClick={() => setShowConfirmClose(false)}
                      className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Keep Editing
                    </button>
                    <button
                      onClick={handleForceClose}
                      className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-500 transition-colors"
                    >
                      Discard
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default SendEstimateModal;
